/**
 * Distance-banded LOD + cull policy (WR.3.4) — pure, no `three`, node-safe. It
 * operates on `AssetEntry` / `LodBand` (the asset-resolution layer), which is why
 * it sits with the loader/manifest rather than in scene/: the loader consumes
 * `lodHandle` to fetch the right artifact, and the scene builder consumes
 * `selectLod` / `cullDistance` to distance-cull and (later) swap LODs per frame.
 *
 * Spec §5 ("wire distance-banded LOD from streamingCells.lodBands"): bands are
 * near->far (ascending `maxDistance`), the exact `streamingCells.lodBands` shape.
 *
 * LOD-band source (the conservative boundary call): the join-ack `WorldDescriptor`
 * does NOT carry `streamingCells`, and `streamingCells` is stripped from the
 * client content pack (client-pack leak test), so `streamingCells.lodBands` is
 * not client-reachable today. WR.3.4 ships a provisional client `DEFAULT_LOD_BANDS`
 * shaped EXACTLY as `LodBand[]`, and every consumer takes an optional `bands`
 * override — so when WR.4/WR.5 streaming delivers content `lodBands` to the
 * client it flows in with no rewrite, and WR.3.4 stays off the protocol/projection
 * boundary (spec §2/§4: WR.3.1–3.4 need no protocol change).
 *
 * Import boundary (AD-122): shared types via `@great-cloud/shared/client` only.
 */

import type { AssetEntry, LodBand } from '@great-cloud/shared/client';

/** Sentinel `selectLod` result: the thing is beyond the last band → cull it. */
export const LOD_CULLED = -1;

/**
 * The provisional default LOD ladder (matches the world-generation reference:
 * `[ {80,0}, {200,1}, {600,2} ]`). Used until content `streamingCells.lodBands`
 * is delivered client-side (WR.4/WR.5 streaming); every consumer takes an
 * optional override so that delivery needs no rewrite.
 */
export const DEFAULT_LOD_BANDS: readonly LodBand[] = [
  { maxDistance: 80, lod: 0 },
  { maxDistance: 200, lod: 1 },
  { maxDistance: 600, lod: 2 },
];

/**
 * Pick the LOD index for a camera distance: the first band whose `maxDistance`
 * covers it (near->far), or `LOD_CULLED` when it is beyond every band. Empty
 * bands ⇒ always lod 0 (no LOD/cull policy → draw at full detail, never cull).
 */
export function selectLod(distance: number, bands: readonly LodBand[] = DEFAULT_LOD_BANDS): number {
  if (bands.length === 0) return 0;
  for (const band of bands) {
    if (distance <= band.maxDistance) return band.lod;
  }
  return LOD_CULLED;
}

/** The farthest LOD band's `maxDistance` = the distance-cull radius (Infinity if no bands). */
export function cullDistance(bands: readonly LodBand[] = DEFAULT_LOD_BANDS): number {
  if (bands.length === 0) return Infinity;
  let max = 0;
  for (const band of bands) max = Math.max(max, band.maxDistance);
  return max;
}

/**
 * Resolve the delivery handle for an entry at a LOD index, consuming
 * `AssetEntry.lods[]` (near->far). Clamps to the available range (a pack with
 * fewer LODs than bands reuses its farthest), falls back to `entry.url`, and
 * returns `null` for `LOD_CULLED` or an entry with no handle at all (⇒ the
 * caller keeps/hides its placeholder — the graceful-degradation rule, §8).
 */
export function lodHandle(entry: AssetEntry, lod: number): string | null {
  if (lod === LOD_CULLED) return null;
  const lods = entry.lods;
  if (lods && lods.length > 0) {
    const i = Math.max(0, Math.min(lod, lods.length - 1));
    return lods[i] ?? entry.url ?? null;
  }
  return entry.url ?? null;
}
